import React, {
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer } from 'antd';
import { Account, AccountType } from '../../dto';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { useEvents } from '../../common/EventContext';
import { getCurrencies } from '../../common/SelectEnums';

export type AccountCreateModalInstance = {
  create: (accountTypeId?: string) => void;
  edit: (account: Account) => void;
};

type Props = {
  accountTypes?: AccountType[];
};

const AccountCreateModal: ForwardRefRenderFunction<
  AccountCreateModalInstance,
  Props
> = (props, ref) => {
  const business = useBusinessContext();
  const [visible, setVisible] = useState<boolean>(false);
  const [accountTypes, setAccountTypes] = useState<AccountType[]>(
    props.accountTypes ?? [],
  );
  const [account, setAccount] = useState<Account>();
  const connection = useConnection();
  const { businessId, currency } = useBusinessContext();
  const [sites, setSites] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState<boolean>(false);
  const events = useEvents();
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  useEffect(() => {
    if (props.accountTypes) {
      setAccountTypes(props.accountTypes ?? []);
    }
  }, [props.accountTypes]);
  useImperativeHandle(
    ref,
    () => ({
      create: (accountTypeId?: string) => {
        setVisible(true);
        form.resetFields();
        form.setFieldsValue({ currency, accountTypeId });
        setMode('create');
        setSites((prevState) => {
          if (prevState.length === 0) {
            connection
              .get(`sites/${businessId}/for/select`)
              .then((value) => setSites(value.data));
          }
          return prevState;
        });
      },
      edit: (account: Account) => {
        setVisible(true);
        form.resetFields();
        form.setFieldsValue(account);
        setAccount(account);
        setMode('edit');
        setSites((prevState) => {
          if (prevState.length === 0) {
            connection
              .get(`sites/${businessId}/for/select`)
              .then((value) => setSites(value.data));
          }
          return prevState;
        });
      },
    }),
    [businessId, connection, currency, form],
  );

  const schema: FormSchema = useMemo(() => {
    return [
      {
        id: 'accountTypeId',
        name: 'Account Type',
        type: 'select2',
        props: {
          showSearch: true,
          disabled: mode === 'edit',
          placeholder: 'Select Account Type',
          optionFilterProp: 'label',
          options: [
            'Assets',
            'Liabilities',
            'Income',
            'Expenses',
            'Equity',
          ].map((acc) => ({
            label: acc,
            options: accountTypes
              .filter((a) => a.accountCategory === acc)
              .map((a) => ({
                label: a.name,
                value: a.accountTypeId,
              })),
          })),
        },
        options: {
          rules: [{ required: true, message: 'Account Type is required' }],
        },
      },
      {
        id: 'name',
        name: 'Account Name',
        type: 'string',
        props: {
          placeholder: 'The account name',
        },
        options: {
          rules: [{ required: true, message: 'Account Name is required' }],
        },
      },
      {
        id: 'currency',
        name: 'Account Currency',
        type: 'select2',
        props: {
          disabled: mode === 'edit',
          showSearch: true,
          optionFilterProp: 'label',
          options: getCurrencies(business),
        },
        visible: (value: any) => {
          const accType = accountTypes.find(
            (v) => v.accountTypeId === value?.accountTypeId,
          );
          return !!accType?.multiCurrency;
        },
        options: {
          rules: [{ required: true, message: 'Account Currency is required' }],
        },
      },
      {
        id: 'siteId',
        name: 'Account Site',
        type: 'select2',
        props: {
          disabled: mode === 'edit',
          showSearch: true,
          allowClear: true,
          placeholder: 'All Sites',
          optionFilterProp: 'label',
          options: sites,
        },
      },
      {
        id: 'displayId',
        name: 'Account ID',
        type: 'string',
      },
      {
        id: 'description',
        name: 'Description',
        type: 'text',
      },
    ];
  }, [accountTypes, sites, mode, business]);

  const submit = useCallback(
    (value: any) => {
      setSaving(true);
      if (mode === 'create') {
        const accType = accountTypes.find(
          (v) => v.accountTypeId === value?.accountTypeId,
        );
        connection
          .post(`accounts/${businessId}`, {
            ...value,
            currency: accType?.multiCurrency
              ? (value.currency ?? currency)
              : currency,
          })
          .then((res) => {
            events.emit('ACCOUNT', { action: 'CREATE', data: res.data });
            setVisible(false);
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .patch(`accounts/${businessId}/${account?.accountId}`, {
            name: value.name,
            description: value.description,
            displayId: value.displayId,
          })
          .then((res) => {
            events.emit('ACCOUNT', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .finally(() => setSaving(false));
      }
    },
    [accountTypes, businessId, connection, currency, events, mode, account],
  );

  return (
    <Drawer
      title="Add an Account"
      width={600}
      open={visible}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={() => setVisible(false)}>Cancel</Button>
          <Button loading={saving} type="primary" onClick={() => form.submit()}>
            Save
          </Button>
        </div>
      }
      onClose={() => setVisible(false)}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Items schema={schema} />
      </Form>
    </Drawer>
  );
};

export const useAccountCreateModal = () =>
  useRef<AccountCreateModalInstance>(null);

export default React.forwardRef<AccountCreateModalInstance, Props>(
  AccountCreateModal,
);
